import {useCallback, useMemo, useState} from 'react';

import {ACTION_TYPES, EVENT_NAMES, WATCHLIST_ACTION} from '/enum/tealium.enum';
import {
  addCoachToWatchlist,
  getViewTeams,
  removeCoachFromWatchlist,
  setSelectedTab,
} from '/redux/action/myTeams.action';
import {setIsViewTeam} from '/redux/action/myTeams.action';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {CoachInformation} from '../TeamOrgReport';

import {useMyTeamsContext} from './useMyTeamsContext';

type UseMyTeamsBusinessProps = {
  downlineCoachIds: string[];
  filtersData?: Record<string, any>;
  reportParams?: any[];

  navigation: any;
  route: any;

  userId: string | number;
  lastPaidRank: string;
  highestAchievedRank: string;
  coachCurrentRank: string;

  isViewTeam: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  myTeamsData?: CoachInformation[];
  updatedItem?: (coach: CoachInformation) => CoachInformation;

  onPress?: (coach: CoachInformation) => void;
  count: number;
  previousScreenName: string;
  currentScreenName: string;
  reportFilterCount?: number;
  isWatchlistScreen?: boolean;
  tabsValue?: string;
  normalizeCoach?: (coach: CoachInformation) => CoachInformation;
};

export const useMyTeamsBusiness = ({
  downlineCoachIds,
  navigation,
  route,
  userId,
  lastPaidRank,
  highestAchievedRank,
  coachCurrentRank,
  isViewTeam,
  count,
  //onPress,
  currentScreenName,
  previousScreenName,
  setIsLoading,
  reportFilterCount,
  isWatchlistScreen,
  tabsValue,
}: UseMyTeamsBusinessProps) => {
  const [viewTeamLoadingId, setViewTeamLoadingId] = useState(null);
  const [loadingStarId, setLoadingStarId] = useState<Set<string>>(new Set());
  const [selectedCoachId, setSelectedCoachId] = useState(null);
  const {dispatch} = useMyTeamsContext();
  const sendEvent = useMemo(
    () =>
      getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.onPageLoad,
      }),
    [],
  );

  const formatNumber = useCallback(value => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US').format(value);
  }, []);

  const updatedItem = useCallback(coach => {
    if (coach.isParentCoach) return coach;
    if (coach.level) return {...coach, level: {...coach.level, level: 1}};
    return {...coach, level: {level: 1}};
  }, []);

  const handleViewTeamPress = useCallback(
    async (coach: CoachInformation) => {
      const coachId = String(coach.id);

      setViewTeamLoadingId(coach.id);
      setSelectedCoachId(coach.id);
      if (!isWatchlistScreen) {
        setIsLoading?.(true);
      }

      sendEvent({
        currentScreenName,
        previousScreenName,
        lastPaidRank,
        coach_highest_rank: highestAchievedRank,
        coach_current_rank: coachCurrentRank,
        coach_id: String(userId),
        buttonName: 'View Team',
      });

      try {
        await dispatch(
          getViewTeams({
            coachId,
            pageNumber: 1,
            ...(isWatchlistScreen
              ? {tabsValue}
              : {
                  reportName: 'VIEW_TEAMS',
                  reportParams: {},
                  appendData: false,
                  isDrilldown: false,
                }),
          }),
        );

        const navParams = {
          title: `${coach.firstName} ${coach.lastName}'s Team`,
          isViewTeam: true,
          coachId: coach.id,

          // onPress,
          downlineCoachIds,
          count,
          lastPaidRank,
          highestAchievedRank,
          coachCurrentRank,
          userId,

          ...(isWatchlistScreen
            ? {
                watchListedCoachesCount: reportFilterCount,
              }
            : {
                resetFiltersAndSorting: true,
              }),
        };

        navigation.push('myteams-data', navParams);
        dispatch(setSelectedTab('my-teams'));
        dispatch(setIsViewTeam(true));
      } finally {
        setViewTeamLoadingId(null);
      }
    },
    [
      isWatchlistScreen,
      tabsValue,
      navigation,
      downlineCoachIds,
      count,
      lastPaidRank,
      highestAchievedRank,
      coachCurrentRank,
      userId,
      currentScreenName,
      previousScreenName,
      setIsLoading,
      sendEvent,
      reportFilterCount,
      dispatch,
    ],
  );

  const toggleAddWatchlist = useCallback(
    (coach: CoachInformation) => {
      const coachId = String(coach.id);

      if (isWatchlistScreen) {
        setLoadingStarId(coach.id);
      } else {
        setLoadingStarId(prev => new Set(prev).add(coachId));
      }

      const isInWishlist = downlineCoachIds.includes(coachId);

      const actionType = isWatchlistScreen
        ? ACTION_TYPES.remove
        : isInWishlist
        ? ACTION_TYPES.remove
        : ACTION_TYPES.add;

      let actionReportName;

      if (isWatchlistScreen) {
        actionReportName = WATCHLIST_ACTION.watchList;
      } else if (isViewTeam) {
        actionReportName = WATCHLIST_ACTION.viewTeam;
      } else {
        actionReportName = WATCHLIST_ACTION.myTeams;
      }

      const sendEventProfileUpdate =
        getTealiumUtilDefaultInstance().generateTrackEvent({
          eventName: EVENT_NAMES.profileUpdate,
        });

      sendEventProfileUpdate({
        updated_fields: 'watchlist',
        userId: coachId,
        report_name: actionReportName,
        action: actionType,
        lastPaidRank,
        coach_highest_rank: highestAchievedRank,
        coachCurrentRank,
        coach_id: String(userId ?? ''),
      });

      const action =
        actionType === ACTION_TYPES.remove
          ? dispatch(removeCoachFromWatchlist({downlineCoachId: coachId}))
          : dispatch(addCoachToWatchlist({downlineCoachId: coachId}));

      Promise.resolve(action).finally(() => {
        if (isWatchlistScreen) {
          setLoadingStarId(null);
        } else {
          setLoadingStarId(prev => {
            const next = new Set(prev);
            next.delete(coachId);
            return next;
          });
        }
      });

      const currentCount = route.params?.count ?? 0;

      const newCount =
        actionType === ACTION_TYPES.remove
          ? currentCount - 1
          : currentCount + 1;

      (navigation as any).setParams({count: newCount});
    },
    [
      downlineCoachIds,
      isViewTeam,
      isWatchlistScreen,
      lastPaidRank,
      highestAchievedRank,
      coachCurrentRank,
      userId,
      route.params?.count,
      navigation,
      dispatch,
    ],
  );

  return {
    viewTeamLoadingId,
    loadingStarId,
    selectedCoachId,
    handleViewTeamPress,
    toggleAddWatchlist,
    formatNumber,
    updatedItem,
    setSelectedCoachId,
  };
};
